import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import AryeoLandingHelmet from 'common/helmets/AryeoLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import Aryeo1 from 'img/landing/aryeo-1.webp';
import AryeoWordmark from 'img/landing/aryeo-wordmark.webp';
import Chelsea from 'img/landing/chelsea.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const AryeoLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Aryeo Case Study');
  }, [trackEvent]);

  return (
    <div className="landingAryeo landingCustomersPage">
      <AryeoLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Aryeo uses Canny to reduce support queries by 20%" />
          <div className="headerImage">
            <div className="nametag">
              <div className="name">Chelsea Darby</div>
              <div className="title">Customer Success Lead at&nbsp;Aryeo</div>
            </div>
            <LazyLoadedImage loading="eager" src={Chelsea} alt="Chelsea Darby" />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              The best business ideas are born from people fixing issues they’ve experienced for
              themselves. This is how Aryeo came to be—the founders were real estate photographers
              who realized there was no convenient way to deliver their work to their customers,
              usually agents and&nbsp;brokerages.
            </p>
            <p>
              So, they created a solution for real estate photographers to upload, create, and
              deliver listing media content to agents. Today, Aryeo hosts 1% of the US’ real estate
              media&nbsp;content.
            </p>
            <p>
              Chelsea Darby is the Customer Success Lead at Aryeo. She joined last year, and took
              over all of Aryeo’s customer-facing efforts, from support to onboarding to
              reducing&nbsp;churn.
            </p>
            <p>
              User feedback is extremely important and valued by the whole team, including when it
              comes to product&nbsp;planning.
            </p>
            <p className="quote">
              &ldquo;We work hard to ensure that the needs of our customers are being heard at the
              time that we’re developing the next product&nbsp;cycle.&rdquo;
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              Before Canny, Aryeo gathered feedback mostly through Intercom. From there, it was
              documented elsewhere as a separate list of requests and&nbsp;issues.
            </p>
            <p>
              When companies start recording feedback, this is a common first strategy. However, it
              starts to break down at scale—especially for a business with a fairly vocal
              user&nbsp;base.
            </p>
            <p className="quote">
              &ldquo;It started getting to the point where feedback was falling on
              deaf&nbsp;ears.&rdquo;
            </p>
            <p>
              It was also difficult to keep track of multiple people asking for the same thing,
              which made it hard to communicate the impact of different requests to the&nbsp;team.
            </p>
            <p>
              And, for a company that cares deeply about their customers, letting them down was not
              an&nbsp;option.
            </p>
            <p className="quote">
              &ldquo;I can’t explain how awful it feels to be on the receiving end of all those
              customer requests, and all you can say is: ‘I’ve shared it with the&nbsp;team.’&rdquo;
            </p>
            <p>
              Besides messy documentation that was hard to share internally, recording feedback
              manually was also taking a toll on the support&nbsp;team.
            </p>
            <p className="quote">
              &ldquo;My inbox was clogged with feature requests, and I found it hard to focus,
              because I had to constantly be logging feedback&nbsp;separately.&rdquo;
            </p>
            <p>
              Since Chelsea had recently joined Aryeo and had to focus on setting up the entire
              customer success journey and experience, she decided it was time to look for a
              better&nbsp;solution.
            </p>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              Another member of Aryeo’s team had used Canny at a different company to solve a
              similar problem, so he suggested they check it&nbsp;out.
            </p>
            <p className="quote">
              &ldquo;I looked at it, and immediately said: ‘This is exactly what we
              need&nbsp;’&rdquo;
            </p>
            <p>
              Besides being just the right solution for the issues they were facing, Canny was also
              easy to set up without external&nbsp;help.
            </p>
            <p className="quote">
              &ldquo;I’ve set up multiple software systems before—this was by far the
              easiest&nbsp;one.&rdquo;
            </p>
            <LazyLoadedImage alt="Aryeo logo" className="border" src={Aryeo1} />
            <p>
              Chelsea was impressed with the fact that she was able to set Canny up on her own,
              without extensive technical&nbsp;skills.
            </p>
            <p>
              Now, Canny is being used daily—not just by Chelsea to manage feedback, but also the
              technical co-founder at&nbsp;Aryeo.
            </p>
            <p>
              He’s directly seeing all of the feedback and requests. Since he’s the one driving a
              lot of product decisions, the visibility into what is really needed is&nbsp;crucial.
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              One of the main benefits has been that the users finally feel like they’re being
              involved in the&nbsp;process.
            </p>
            <p>
              Canny has provided an interactive space where the users really feel like they have a
              voice, and that they’re being listened&nbsp;to.
            </p>
            <p className="quote">
              &ldquo;It creates that long term relationship and loyalty that we’re
              striving&nbsp;for.&rdquo;
            </p>
            <p>
              Besides effective communication, Canny has also helped identify things that could be
              going&nbsp;better.
            </p>
            <p>
              Feedback gives the team a constant pulse on how they’re doing, and what they could
              improve&nbsp;on.
            </p>
            <p className="quote">
              &ldquo;For example, if we’re losing customers, there’s a story there with data about
              why that could&nbsp;be.&rdquo;
            </p>
            <p>Canny has also saved the support team a fair amount of&nbsp;time.</p>
            <p className="quote">
              &ldquo;Canny has reduced the number of inbounds in our support inbox by 20%, which is
              a big&nbsp;deal.&rdquo;
            </p>
            <p>
              This means less time handling feature requests manually and more time on improving the
              customer experience on a deeper&nbsp;level.
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage alt="Aryeo Wordmark" className="aryeoWordmark" src={AryeoWordmark} />
            Aryeo is a platform to manage, share, and collaborate on the content created to
            market&nbsp;properties.
            <a href="https://www.aryeo.com/" rel="noopener" target="_blank">
              aryeo.com
            </a>
            <div className="stat">
              <UppercaseHeader>Location</UppercaseHeader>
              <p>Boston, MA</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2020</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>1,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};
export default AryeoLanding;
